import { useMemo } from 'react';

import { localDate, type Opportunity } from '../../shared/opportunity';
import { attendedSpan } from './attendance';
import { buildDayPlan } from './conflicts';
import type { AttendanceStore } from './useAttendance';

/**
 * The four numbers about the user's week, worked out once for whoever asks.
 *
 * My Schedule's sidebar has shown them since the start; the Sept 19 account
 * page shows the same four on a phone, where there is no sidebar to show them
 * in. Two copies of this arithmetic would drift the first time a conflict rule
 * changed, and the two would then disagree on the same screen.
 *
 * "Days covered" used to be a fifth. Sam cut it on Sept 19 — from the app, not
 * just from the account page.
 */
export interface WeekStats {
  scheduled: number;
  saved: number;
  overlaps: number;
  tight: number;
}

export function weekStats(
  scheduled: readonly Opportunity[],
  saved: readonly Opportunity[],
  trims: AttendanceStore['trims'],
): WeekStats {
  const byDay = new Map<string, Opportunity[]>();
  for (const event of [...scheduled, ...saved]) {
    const day = localDate(event);
    const list = byDay.get(day);
    if (list) list.push(event);
    else byDay.set(day, [event]);
  }

  let overlaps = 0;
  let tight = 0;
  for (const [day, events] of byDay) {
    const plan = buildDayPlan(day, events, (e) => attendedSpan(e, trims));
    overlaps += plan.overlaps.length;
    tight += plan.tight.length;
  }

  return { scheduled: scheduled.length, saved: saved.length, overlaps, tight };
}

export function useWeekStats(
  scheduled: readonly Opportunity[],
  saved: readonly Opportunity[],
  trims: AttendanceStore['trims'],
): WeekStats {
  return useMemo(() => weekStats(scheduled, saved, trims), [scheduled, saved, trims]);
}

/**
 * The labels, in one place and in the mockup's longer wording (Sam, Sept 19):
 * "Conflicts" on its own says nothing on a page that is not already a schedule.
 */
export const WEEK_STAT_ROWS = [
  { key: 'scheduled', label: 'Events on my schedule' },
  { key: 'saved', label: 'Events saved for later' },
  { key: 'overlaps', label: 'Schedule conflicts' },
  { key: 'tight', label: 'Tight connections' },
] as const satisfies readonly { key: keyof WeekStats; label: string }[];

/** The two that are a problem when they are not zero. */
export const isWarningStat = (key: keyof WeekStats): boolean => key === 'overlaps' || key === 'tight';
